import { Reorder, useDragControls, useReducedMotion } from 'framer-motion';
import { AlbumArt } from './AlbumArt';
import { RowSkeleton } from './SearchResults';
import { CheckIcon, GripIcon, TrashIcon } from './icons';
import { formatMs } from '../../utils/formatMs';

function SectionTitle({ title, count, hint }) {
  return (
    <div className="mb-4 flex items-baseline justify-between gap-3">
      <h2 className="font-serif text-[22px] font-medium text-lounge-text">
        {title}
        {count != null && (
          <span className="ml-2 font-mono text-xs font-normal tracking-wider text-lounge-brass">({count})</span>
        )}
      </h2>
      {hint && <span className="font-mono text-[11px] tracking-wider text-lounge-stone">{hint}</span>}
    </div>
  );
}

function Chip({ className, children }) {
  return (
    <span className={`shrink-0 rounded-sm px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${className}`}>
      {children}
    </span>
  );
}

function StagedRow({ song, onRemove, reduce }) {
  const controls = useDragControls();

  return (
    <Reorder.Item
      value={song}
      dragListener={false}
      dragControls={controls}
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduce ? undefined : { opacity: 0, x: -24 }}
      whileDrag={{ scale: 1.02, boxShadow: '0 16px 48px -8px rgba(0,0,0,0.8)' }}
      className="relative flex items-center gap-2 rounded-md border border-lounge-line bg-lounge-card p-2 pl-0"
    >
      <button
        type="button"
        aria-label="Arrastrar para ordenar"
        onPointerDown={(e) => controls.start(e)}
        className="flex h-11 w-8 shrink-0 cursor-grab touch-none items-center justify-center text-lounge-stone
                   active:cursor-grabbing"
      >
        <GripIcon className="h-4 w-4" />
      </button>
      <AlbumArt image={song.image} alt="" className="w-14" />
      <div className="ml-1 min-w-0 flex-1">
        <p className="truncate font-inter text-[15px] font-medium text-lounge-text">{song.name}</p>
        <p className="truncate font-inter text-sm text-lounge-stone">{song.artist}</p>
        <div className="mt-1 flex items-center gap-2">
          {song.durationMs > 0 && (
            <span className="font-mono text-[11px] tabular-nums text-lounge-stone">{formatMs(song.durationMs)}</span>
          )}
          <Chip className="bg-lounge-raised text-lounge-brass">Sin enviar</Chip>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onRemove(song.uri)}
        aria-label={`Quitar ${song.name}`}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-lounge-stone
                   transition-colors hover:bg-lounge-raised hover:text-stop-soft"
      >
        <TrashIcon className="h-[18px] w-[18px]" />
      </button>
    </Reorder.Item>
  );
}

function SentRow({ track }) {
  return (
    <li className={`flex items-center gap-3 border-b border-white/[0.04] py-2.5 last:border-0
                    ${track.played ? 'opacity-50' : ''}`}>
      <AlbumArt image={track.image} alt="" className="w-11" />
      <div className="min-w-0 flex-1">
        <p className={`truncate font-inter text-[15px] text-lounge-text ${track.played ? 'line-through decoration-lounge-smoke' : ''}`}>
          {track.name}
        </p>
        <p className="truncate font-inter text-sm text-lounge-stone">{track.artist}</p>
      </div>
      {track.played
        ? <Chip className="flex items-center gap-1 text-felt"><CheckIcon className="h-3.5 w-3.5" />Sonó</Chip>
        : <Chip className="border border-lounge-brass/40 text-lounge-brass">En cola</Chip>}
    </li>
  );
}

// Dos secciones hermanas (Tu selección / En cola); RoomView las ubica en el layout.
export function SongList({ queue, sent, sending, sendingFlow, pendingCount = 3,
                           sendError, onReorder, onRemove, onSend }) {
  const reduce = useReducedMotion();

  // Pendientes primero (en orden de envío), luego las que ya sonaron (más reciente arriba).
  const waiting = sent.filter((s) => !s.played);
  const played  = sent.filter((s) => s.played).reverse();
  const showSelection = queue.length > 0 && !sendingFlow;

  return (
    <>
      {showSelection && (
        <section aria-label="Tu selección">
          <SectionTitle title="Tu selección" count={queue.length} hint={queue.length > 1 ? 'Arrastra para ordenar' : null} />
          <Reorder.Group axis="y" values={queue} onReorder={onReorder} className="flex flex-col gap-2">
            {queue.map((song) => (
              <StagedRow key={song.uri} song={song} onRemove={onRemove} reduce={reduce} />
            ))}
          </Reorder.Group>

          {/* En móvil el botón queda fijo sobre el área segura, al alcance del pulgar */}
          <div className="fixed inset-x-0 bottom-0 z-30 border-t border-lounge-line bg-lounge-base/85 px-5 pt-3
                          pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-xl
                          lg:static lg:mt-4 lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none">
            <button
              type="button"
              onClick={onSend}
              disabled={sending}
              className="mx-auto flex h-14 w-full max-w-[480px] items-center justify-center rounded bg-lounge-brass
                         font-inter text-base font-semibold text-lounge-base transition-all duration-200
                         hover:bg-amber hover:shadow-brass active:scale-[0.98] disabled:opacity-60
                         lg:max-w-none"
            >
              {sending ? 'Enviando…' : `Enviar a la rockola (${queue.length})`}
            </button>
          </div>
        </section>
      )}

      {sendError && (
        <p role="alert" className="rounded border border-stop/30 bg-stop/10 px-4 py-3 text-center
                                   font-inter text-sm text-stop-soft">
          {sendError}
        </p>
      )}

      {(sent.length > 0 || sendingFlow) && (
        <section id="en-cola" aria-label="En cola" className="scroll-mt-24">
          <SectionTitle
            title="En cola"
            hint={waiting.length > 0 ? `${waiting.length} en espera` : null}
          />
          {sendingFlow && (
            <div className="mb-2 flex flex-col gap-2" aria-busy="true">
              {Array.from({ length: pendingCount }).map((_, i) => <RowSkeleton key={i} />)}
            </div>
          )}
          {sent.length > 0 && (
            <ul className="rounded-md border border-lounge-line bg-lounge-chassis px-3">
              {[...waiting, ...played].map((track) => (
                <SentRow key={`${track.trackUri}-${track.sentAt}`} track={track} />
              ))}
            </ul>
          )}
        </section>
      )}
    </>
  );
}
